import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { realtimeSocket, type RealtimeEvent, type RealtimeCallback } from "@/lib/realtimeSocket";

// Re-export types for convenience
export type { RealtimeEvent, RealtimeCallback };

interface RealtimeContextValue {
  connected: boolean;
  subscribeToInbox: (callback: RealtimeCallback) => () => void;
  subscribeToCalendar: (callback: RealtimeCallback) => () => void;
  subscribeToConversation: (conversationId: string) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [connected, setConnected] = useState(realtimeSocket.connected);

  // Connect/disconnect based on auth state
  useEffect(() => {
    if (authLoading) return;

    if (user?.id) {
      // Connect the singleton
      realtimeSocket.connect(user.id);
    } else {
      // User logged out - force disconnect
      realtimeSocket.forceDisconnect();
    }
  }, [user?.id, authLoading]);

  // Cleanup: destroy socket on provider unmount (app shutdown)
  // Separate effect with empty deps to only run on unmount
  useEffect(() => {
    return () => {
      realtimeSocket.destroy();
    };
  }, []);

  // Subscribe to connection state changes
  useEffect(() => {
    return realtimeSocket.subscribeToConnectionChange(setConnected);
  }, []);

  // Wrap singleton methods for React components
  const subscribeToInbox = useCallback((callback: RealtimeCallback) => {
    return realtimeSocket.subscribeToInbox(callback);
  }, []);

  const subscribeToCalendar = useCallback((callback: RealtimeCallback) => {
    return realtimeSocket.subscribeToCalendar(callback);
  }, []);

  const subscribeToConversation = useCallback((conversationId: string) => {
    realtimeSocket.subscribeToConversation(conversationId);
  }, []);

  const value: RealtimeContextValue = {
    connected,
    subscribeToInbox,
    subscribeToCalendar,
    subscribeToConversation,
  };

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeContext() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error("useRealtimeContext must be used within a RealtimeProvider");
  }
  return context;
}
